import type { CalendarFeed, CalendarItem } from "@ronin/contracts";
import { layoutSpans, layoutTimed, zonedParts } from "@ronin/core";
import { cn } from "@ronin/ui";
import { Cake, ChevronDown, ChevronUp, Flag } from "lucide-react";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { allDaySpans, blockTimes, holidaysByDay, timedBlocks } from "../../lib/calendar-items.js";
import { dayLabel, longDate, shortWeekday } from "../../lib/calendar-view.js";
import { useElementHeight } from "../../lib/use-element-height.js";
import { ItemChip } from "./item-chip.js";
import { describeItem, isBirthday, itemColor, itemTitle } from "./item-style.js";

/** Hour rows stretch to fill the calendar box, but never get smaller than this (px). */
const MIN_HOUR = 32;
const LANE = 22;
const MAX_LANES = 3;
const MIN_MINUTES = 30; // drawn minimum, so the title fits
/** The hour grid shows 07:00–23:00 and extends around earlier or later events (FR4). */
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 23;

interface TimeGridProps {
  days: readonly string[];
  items: readonly CalendarItem[];
  feeds: ReadonlyMap<string, CalendarFeed>;
  timeZone: string;
  today: string;
  /** Phones (spec03 FR6): the whole week in a narrow gutter, compact headers and text. */
  compact?: boolean;
  onCreate: (date: string, time?: string) => void;
  onOpenItem: (item: CalendarItem) => void;
  onOpenDay: (date: string) => void;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Re-renders every minute for the "now" line. */
function useNowMinutes(timeZone: string) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return zonedParts(now, timeZone);
}

/**
 * Week and day view (spec03 FR4): an "All day" section on top that never covers the
 * hours, then a scrolling 24-hour grid with overlapping events side by side.
 */
export function TimeGrid({
  days,
  items,
  feeds,
  timeZone,
  today,
  compact = false,
  onCreate,
  onOpenItem,
  onOpenDay,
}: TimeGridProps) {
  const [expanded, setExpanded] = useState(false);
  const now = useNowMinutes(timeZone);
  const first = days[0] ?? today;
  const columns = days.length;

  const spans = useMemo(() => allDaySpans(items), [items]);
  const placed = useMemo(() => layoutSpans(spans, first, columns), [spans, first, columns]);
  const blocks = useMemo(() => timedBlocks(items, timeZone), [items, timeZone]);
  const holidays = useMemo(() => holidaysByDay(items, days), [items, days]);

  const lanes = placed.reduce((max, p) => Math.max(max, p.lane + 1), 0);
  const visibleLanes = expanded ? lanes : Math.min(lanes, MAX_LANES);
  const hiddenPerDay = days.map(
    (_, column) =>
      placed.filter(
        (p) => p.lane >= MAX_LANES && p.column <= column && p.column + p.length > column,
      ).length,
  );
  const anyHidden = hiddenPerDay.some((n) => n > 0);

  // 07:00–23:00, widened to whole hours around any event of the shown days.
  const shownBlocks = days.flatMap((day) => blocks.get(day) ?? []);
  const firstHour = Math.min(DAY_START_HOUR, ...shownBlocks.map((b) => Math.floor(b.start / 60)));
  const lastHour = Math.max(
    DAY_END_HOUR,
    ...shownBlocks.map((b) => Math.ceil(Math.max(b.end, b.start + MIN_MINUTES) / 60)),
  );
  const hours = Array.from({ length: lastHour - firstHour }, (_, i) => firstHour + i);
  const scrollRef = useRef<HTMLDivElement>(null);
  const available = useElementHeight(scrollRef);
  const hourPx = Math.max(MIN_HOUR, Math.floor(available / hours.length) || MIN_HOUR);
  const gridHeight = hours.length * hourPx;
  const toPx = (minutes: number) => ((minutes - firstHour * 60) / 60) * hourPx;

  const gutter = compact ? "2.25rem" : "4rem";
  const gridColumns = { gridTemplateColumns: `${gutter} repeat(${columns}, minmax(0, 1fr))` };

  function createAt(day: string, event: MouseEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const minutes = firstHour * 60 + Math.floor(((event.clientY - rect.top) / hourPx) * 2) * 30;
    const clamped = Math.min(Math.max(minutes, 0), 23 * 60 + 30);
    onCreate(day, `${pad2(Math.floor(clamped / 60))}:${pad2(clamped % 60)}`);
  }

  return (
    <div className="flex h-full flex-col border bg-card">
      {/* Day headers with holidays */}
      <div className="grid border-b" style={gridColumns}>
        <div />
        {days.map((day) => {
          const dayHolidays = holidays.get(day);
          const school = dayHolidays?.school[0];
          return (
            <div
              key={day}
              className={cn(
                "flex min-w-0 flex-col gap-0.5 border-l",
                compact ? "items-center px-0.5 py-1" : "px-2 py-1.5",
                school && "border-t-2 border-t-faint-foreground/40",
              )}
            >
              <button
                type="button"
                aria-label={`${longDate(day)}${day === today ? ", today" : ""}: open day`}
                onClick={() => onOpenDay(day)}
                className={cn(
                  "w-fit px-1 text-xs font-medium tabular-nums text-secondary-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
                  compact && "flex flex-col items-center leading-tight",
                  day === today && "bg-primary text-primary-foreground hover:bg-primary-hover",
                )}
              >
                {compact ? (
                  <>
                    <span className="text-[10px] font-normal">{shortWeekday(day)}</span>
                    <span>{Number(day.slice(8))}</span>
                  </>
                ) : (
                  dayLabel(day)
                )}
              </button>
              {dayHolidays?.public.map((h) => (
                <span
                  key={h.id}
                  title={h.title}
                  className="flex items-center gap-1 truncate text-xs text-muted-foreground"
                >
                  <Flag className="size-3 shrink-0" aria-hidden />
                  <span className={compact ? "sr-only" : "truncate"}>{h.title}</span>
                </span>
              ))}
              {school && !compact && (
                <span className="truncate text-[10px] text-faint-foreground">{school.title}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* All-day section */}
      <div className="grid border-b" style={gridColumns}>
        <div
          className={cn(
            "flex flex-col items-end justify-between gap-1 py-1.5",
            compact ? "px-1" : "px-2",
          )}
        >
          <span
            className={cn(
              "text-right font-semibold uppercase text-dim-foreground",
              compact ? "text-[9px] leading-tight" : "whitespace-nowrap text-[10px] tracking-wide",
            )}
          >
            All day
          </span>
          {(anyHidden || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? "Show fewer all-day events" : "Show all all-day events"}
              className="flex size-5 items-center justify-center text-dim-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            >
              {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            </button>
          )}
        </div>
        <div
          className="relative border-l"
          style={{
            gridColumn: `span ${columns}`,
            height: Math.max(1, visibleLanes) * LANE + 8 + (!expanded && anyHidden ? 16 : 0),
          }}
        >
          {placed
            .filter((p) => expanded || p.lane < MAX_LANES)
            .map((p) => (
              <ItemChip
                key={p.span.id}
                item={p.span.item}
                feeds={feeds}
                clippedStart={p.clippedStart}
                clippedEnd={p.clippedEnd}
                label={describeItem(p.span.item, timeZone, feeds)}
                onOpen={onOpenItem}
                className="absolute"
                style={{
                  top: 4 + p.lane * LANE,
                  left: `calc(${(p.column / columns) * 100}% + 2px)`,
                  width: `calc(${(p.length / columns) * 100}% - 4px)`,
                }}
              />
            ))}
          {!expanded &&
            hiddenPerDay.map((hidden, column) =>
              hidden > 0 ? (
                <button
                  key={days[column]}
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="absolute bottom-0.5 h-4 truncate px-1 text-left text-[11px] text-dim-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                  style={{
                    left: `${(column / columns) * 100}%`,
                    width: `${100 / columns}%`,
                  }}
                >
                  +{hidden} more
                </button>
              ) : null,
            )}
        </div>
      </div>

      {/* Hour grid */}
      {/* pan-y here too: a scroll container resets touch-action, and swipes must reach the box. */}
      <div ref={scrollRef} className="min-h-0 flex-1 touch-pan-y overflow-y-auto">
        <div className="grid" style={gridColumns}>
          <div className="relative" style={{ height: gridHeight }} aria-hidden>
            {hours.map((hour) => (
              <span
                key={hour}
                className={cn(
                  "absolute tabular-nums text-faint-foreground",
                  compact ? "right-1 text-[9px]" : "right-2 text-[10px]",
                )}
                style={{ top: toPx(hour * 60) + 2 }}
              >
                {pad2(hour)}:00
              </span>
            ))}
          </div>
          {days.map((day) => {
            const dayBlocks = layoutTimed(blocks.get(day) ?? [], MIN_MINUTES);
            return (
              // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a click on an empty slot is a mouse shortcut; the keyboard uses "New event" (N)
              <div
                key={day}
                onClick={(event) => createAt(day, event)}
                className="relative cursor-pointer border-l"
                style={{
                  height: gridHeight,
                  backgroundImage: `repeating-linear-gradient(to bottom, var(--color-border) 0 1px, transparent 1px ${hourPx}px)`,
                }}
              >
                {dayBlocks.map(({ block, column, columns: shared }) => {
                  const color = itemColor(block.item, feeds);
                  const height = (Math.max(block.end - block.start, MIN_MINUTES) / 60) * hourPx;
                  return (
                    <button
                      key={block.id}
                      type="button"
                      aria-label={describeItem(block.item, timeZone, feeds)}
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenItem(block.item);
                      }}
                      className={cn(
                        "absolute flex flex-col overflow-hidden border-l-3 py-0.5 text-left text-foreground ring-1 ring-card hover:brightness-125 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-ring",
                        compact ? "border-l-2 px-0.5 text-[10px] leading-tight" : "px-1.5 text-xs",
                        color.stripe,
                        color.tint,
                      )}
                      style={{
                        top: toPx(block.start),
                        height: height - 1,
                        left: `calc(${(column / shared) * 100}% + 1px)`,
                        width: `calc(${100 / shared}% - 2px)`,
                      }}
                    >
                      <span className="flex min-w-0 items-center gap-1">
                        {isBirthday(block.item) && <Cake className="size-3 shrink-0" aria-hidden />}
                        <span className="truncate font-medium">{itemTitle(block.item)}</span>
                      </span>
                      {height >= hourPx * 0.9 && (
                        <span
                          className={cn(
                            "tabular-nums text-muted-foreground",
                            compact ? "break-words" : "truncate",
                          )}
                        >
                          {blockTimes(block)}
                        </span>
                      )}
                    </button>
                  );
                })}
                {day === today && now.minutes >= firstHour * 60 && now.minutes <= lastHour * 60 && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute right-0 left-0 z-10 h-0.5 bg-ring"
                    style={{ top: toPx(now.minutes) }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
